import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/verticals";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Star, ChatCircle, CalendarBlank, Clock } from "@phosphor-icons/react";

const STATUS = {
  pending: { label: "In attesa", cls: "bg-amber-100 text-amber-700" },
  confirmed: { label: "Confermata", cls: "bg-emerald-100 text-emerald-700" },
  completed: { label: "Completata", cls: "bg-blue-100 text-blue-700" },
  cancelled: { label: "Annullata", cls: "bg-red-100 text-red-700" },
};

const CustomerDashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [reviewFor, setReviewFor] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const load = useCallback(() => {
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && !user) { navigate("/login"); return; }
    if (user) load();
  }, [user, loading, navigate, load]);

  const submitReview = async () => {
    try {
      await api.post("/reviews", { booking_id: reviewFor.booking_id, rating, comment });
      toast.success("Recensione pubblicata!");
      setReviewFor(null); setComment(""); setRating(5);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold">Ciao, {user.name?.split(" ")[0]}</h1>
      <p className="text-muted-foreground">Le tue prenotazioni</p>

      <div className="mt-6 space-y-3" data-testid="bookings-list">
        {bookings.length === 0 && (
          <div className="text-center py-16 border border-dashed rounded-2xl">
            <p className="text-muted-foreground">Nessuna prenotazione ancora.</p>
            <Button className="rounded-full mt-4" onClick={() => navigate("/search")} data-testid="explore-cta">Esplora servizi</Button>
          </div>
        )}
        {bookings.map((b) => {
          const st = STATUS[b.status] || STATUS.pending;
          return (
            <div key={b.booking_id} className="bg-card border border-border rounded-2xl p-4 md:p-5" data-testid={`booking-${b.booking_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading font-semibold">{b.service_name}</h3>
                    <Badge className={`${st.cls} hover:${st.cls} border-0`}>{st.label}</Badge>
                    {b.payment_status === "paid" && <Badge variant="outline" className="text-emerald-600 border-emerald-200">Pagato</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{b.provider_name}</p>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground mt-2">
                    <span className="flex items-center gap-1"><CalendarBlank size={14} />{b.date}</span>
                    <span className="flex items-center gap-1"><Clock size={14} />{b.start_time}–{b.end_time}</span>
                  </div>
                </div>
                <span className="font-bold text-primary">{eur(b.price)}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="rounded-full gap-1" data-testid={`chat-${b.booking_id}`} onClick={() => navigate(`/booking/${b.booking_id}/chat`)}>
                  <ChatCircle size={15} /> Chat
                </Button>
                {b.payment_status === "paid" && !b.has_review && (
                  <Button size="sm" className="rounded-full gap-1" data-testid={`review-${b.booking_id}`} onClick={() => setReviewFor(b)}>
                    <Star size={15} weight="fill" /> Recensisci
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader><DialogTitle>Recensisci {reviewFor?.provider_name}</DialogTitle></DialogHeader>
          <div className="flex gap-1 justify-center py-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
                <Star size={32} weight="fill" className={n <= rating ? "text-amber-400" : "text-muted"} />
              </button>
            ))}
          </div>
          <Textarea placeholder="Racconta la tua esperienza…" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />
          <Button className="rounded-full" onClick={submitReview} data-testid="review-submit">Pubblica recensione</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDashboard;
